import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';

import {closeFirmwareWizard} from '../reducers/modals';
import {
    setStep,
    selectRobot,
    selectVersion,
    selectTransport,
    setProgress,
    resetWizard
} from '../reducers/firmware-wizard';

import MOCK_ROBOTS from '../lib/mstem-mock/robots';
import FIRMWARES from '../lib/mstem-mock/firmwares';

import FirmwareWizardModalComponent from '../components/firmware-wizard-modal/firmware-wizard-modal.jsx';

/**
 * FirmwareWizardModal container (GEN-05 / Phase 2 / Plan 02-05).
 *
 * Drives the 3-step Firmware OTA wizard:
 *   step 0 — robot dropdown (5 mock robots)
 *   step 1 — version dropdown (3 mock versions per robot)
 *   step 2 — mock progress bar 0..100% via setInterval
 *
 * Pitfall #7 fix: redux-throttle middleware coalesces dispatches at 300ms
 * leading+trailing. If we ticked every 50ms (DG-15 nominal), only ~17 of 100
 * dispatches would actually reach the reducer and the bar would jump in big
 * chunks. We instead tick AT the 300ms boundary with ~6% per tick → 17 ticks
 * = ~5.1s total, every dispatch lands.
 *
 * Lifecycle: componentWillUnmount calls clearTimers() (clearInterval +
 * clearTimeout) — covered by the 'no leaked interval' unit test.
 */

// Per Pitfall #7: tick at 300ms intervals (above redux-throttle's 300ms window).
// 17 ticks * 6% each = ~102% over ~5.1s — clamps to 100 at the boundary.
const TICK_INTERVAL_MS = 300;
const TICK_INCREMENT = 6;
const AUTO_CLOSE_DELAY_MS = 3000;

class FirmwareWizardModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleNext',
            'handleBack',
            'handleSelectRobot',
            'handleSelectVersion',
            'handleSelectTransport',
            'handleRequestClose',
            'tickProgress'
        ]);
        this.intervalId = null;
        this.autoCloseId = null;
        this.state = {isComplete: false};
    }
    componentWillUnmount () {
        this.clearTimers();
    }
    clearTimers () {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        if (this.autoCloseId !== null) {
            clearTimeout(this.autoCloseId);
            this.autoCloseId = null;
        }
    }
    startProgress () {
        this.clearTimers();
        this.setState({isComplete: false});
        this.props.onSetProgress(0);
        this.intervalId = setInterval(this.tickProgress, TICK_INTERVAL_MS);
    }
    tickProgress () {
        const next = Math.min(100, this.props.progress + TICK_INCREMENT);
        this.props.onSetProgress(next);
        if (next >= 100) {
            if (this.intervalId !== null) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
            this.setState({isComplete: true});
            this.autoCloseId = setTimeout(this.handleRequestClose, AUTO_CLOSE_DELAY_MS);
        }
    }
    handleNext () {
        if (this.props.step === 0 && this.props.selectedRobotId) {
            this.props.onSetStep(1);
        } else if (this.props.step === 1 && this.props.selectedVersion && this.props.transport) {
            // UC-05 S-3/S-4: transport (Wi-Fi OTA vs USB) must be chosen first.
            this.props.onSetStep(2);
            this.startProgress();
        }
    }
    handleBack () {
        if (this.props.step > 0) {
            this.clearTimers();
            this.setState({isComplete: false});
            this.props.onSetStep(this.props.step - 1);
        }
    }
    handleSelectRobot (robotId) {
        this.props.onSelectRobot(robotId);
    }
    handleSelectVersion (version) {
        this.props.onSelectVersion(version);
    }
    handleSelectTransport (transport) {
        this.props.onSelectTransport(transport);
    }
    handleRequestClose () {
        this.clearTimers();
        this.setState({isComplete: false});
        this.props.onResetWizard();
        this.props.onClose();
    }
    render () {
        return (
            <FirmwareWizardModalComponent
                firmwares={FIRMWARES}
                isComplete={this.state.isComplete}
                progress={this.props.progress}
                robots={MOCK_ROBOTS}
                selectedRobotId={this.props.selectedRobotId}
                selectedVersion={this.props.selectedVersion}
                step={this.props.step}
                transport={this.props.transport}
                onBack={this.handleBack}
                onNext={this.handleNext}
                onRequestClose={this.handleRequestClose}
                onSelectRobot={this.handleSelectRobot}
                onSelectTransport={this.handleSelectTransport}
                onSelectVersion={this.handleSelectVersion}
            />
        );
    }
}

FirmwareWizardModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onResetWizard: PropTypes.func.isRequired,
    onSelectRobot: PropTypes.func.isRequired,
    onSelectTransport: PropTypes.func.isRequired,
    onSelectVersion: PropTypes.func.isRequired,
    onSetProgress: PropTypes.func.isRequired,
    onSetStep: PropTypes.func.isRequired,
    progress: PropTypes.number.isRequired,
    selectedRobotId: PropTypes.string,
    selectedVersion: PropTypes.string,
    step: PropTypes.number.isRequired,
    transport: PropTypes.oneOf(['usb', 'wifi'])
};

const mapStateToProps = state => ({
    step: state.scratchGui.firmwareWizard.step,
    selectedRobotId: state.scratchGui.firmwareWizard.selectedRobotId,
    selectedVersion: state.scratchGui.firmwareWizard.selectedVersion,
    transport: state.scratchGui.firmwareWizard.transport,
    progress: state.scratchGui.firmwareWizard.progress
});

const mapDispatchToProps = dispatch => ({
    onClose: () => dispatch(closeFirmwareWizard()),
    onSetStep: step => dispatch(setStep(step)),
    onSelectRobot: id => dispatch(selectRobot(id)),
    onSelectVersion: v => dispatch(selectVersion(v)),
    onSelectTransport: t => dispatch(selectTransport(t)),
    onSetProgress: p => dispatch(setProgress(p)),
    onResetWizard: () => dispatch(resetWizard())
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(FirmwareWizardModal);
